import type { RouteLocationNormalized } from "vue-router";
import { wheneverOnceAsync } from "ls/composables/wheneverOnceAsync";
import { useAccountService } from "ls/features/user/useAccountService";
import { toValue } from "vue";
import { useRouter } from "vue-router";
import { isFunction } from "@/common/general";

async function routeRequiresAuth(to: RouteLocationNormalized): Promise<boolean> {
  // all routes require auth by default
  if (!to.meta) return true;

  const { requiresAuth } = to.meta;
  if (requiresAuth === undefined) {
    return true;
  }

  if (isFunction(requiresAuth)) {
    return await requiresAuth();
  }
  return requiresAuth;
}

function useRequestStorageAccessGuard() {
  const router = useRouter();

  // Request storage access permission on initial app start
  // This is needed to ensure that the app can access storage in third-party contexts
  // such as iframes or cross-origin embeds.
  // Because even after user has granted permission initially, subsequent visits to the app must re-request it via JS API again.
  let stop: (() => void) | undefined = router.beforeEach(async () => {
    try {
      // check if we have access to third-party, unpartitioned cookies
      const hasAccess = await document.hasStorageAccess();
      if (hasAccess) return;

      // this is third-party context, check if previously requested storage access and it was granted
      const permissionGranted = (await navigator.permissions.query({ name: "storage-access" })).state === "granted";

      // if so, we can re-request it again without user interaction and prompting user
      if (permissionGranted) {
        await document.requestStorageAccess();
      }
    } catch (error) {
      console.warn("Failed to request storage access permission.", error);
    } finally {
      // only need to run this once, so we remove the guard
      stop?.();
      stop = undefined;
    }
  });
}

export function useAuthGuard() {
  useRequestStorageAccessGuard();

  const router = useRouter();
  router.beforeEach(async to => {
    const client = useAccountService();

    if (client.isAuthenticated.value) {
      return true;
    }

    if (!client.isLoading.value) {
      await client.checkSession();
    }

    await wheneverOnceAsync(() => !toValue(client.isLoading));

    if (!(await routeRequiresAuth(to))) {
      if (!client.isAuthenticated.value) {
        await client.authenticateAnonymously();
      }
      return true;
    }

    // Recheck auth status since "checkSession" may change it
    if (client.isAuthenticated.value) {
      return true;
    }

    const loginUrl = client.getLoginFullUrl(to);
    if (!loginUrl) {
      throw new Error("Attempted to navigate to account service for login but URL was undefined");
    }

    window.location.href = loginUrl;

    // use a promise that never resolves to prevent the navigation from completing and Vue Router from throwing an error
    await new Promise(() => {});

    return false;
  });
}
